import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Clock, Thermometer, AlertTriangle, Mail, CalendarDays, Eye, MessageSquare, Tag, GraduationCap, Bug, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { api, fmtDT, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const ICON = { overdue_check: Clock, out_of_range: Thermometer, inspector_visit: Eye, inspector_feedback: MessageSquare, use_by: Tag, training_expiry: GraduationCap, visit_due: Bug, cooling_late: Snowflake };

export default function AlertsBell({ dark, testid = "alerts-bell-button" }) {
  const { can, venueId, setVenueId, user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const allowed = can("manage");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/notifications");
      setData(d);
    } catch (e) {
      /* ignore polling errors */
    }
  }, []);

  useEffect(() => {
    if (!allowed) return undefined;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [allowed, load]);

  if (!allowed) return null;

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.venue_id && n.venue_id !== venueId) setVenueId(n.venue_id);
    nav(n.link);
    load();
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const toggleWeekly = async (enabled) => {
    try {
      await api.put("/me/weekly-report", { enabled });
      setData((d) => ({ ...d, weekly_report: enabled }));
      toast.success(enabled ? "You'll get the weekly report every Monday at 7am" : "Weekly report turned off");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleDigest = async (enabled) => {
    try {
      await api.put("/me/alert-digest", { enabled });
      setData((d) => ({ ...d, digest: enabled }));
      toast.success(enabled ? "You'll get a daily alert summary at 7am" : "Daily alert email turned off");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid={testid} className={cn("ka-press relative rounded-lg p-2", dark ? "text-slate-300 hover:bg-white/10 hover:text-white" : "text-slate-700 hover:bg-slate-100")}>
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span data-testid="alerts-unread-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">{data.unread > 9 ? "9+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-0">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="font-display text-base font-bold">Alerts</span>
          {data.unread > 0 && <button data-testid="alerts-read-all" onClick={readAll} className="text-xs font-semibold text-emerald-700 hover:underline">Mark all read</button>}
        </div>
        <ul data-testid="alerts-list" className="max-h-[420px] overflow-y-auto">
          {data.items.map((n) => {
            const I = ICON[n.kind] || AlertTriangle;
            return (
              <li key={n.id}>
                <button data-testid={`alert-item-${n.id}`} onClick={() => openItem(n)} className={cn("flex w-full gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-slate-50", !n.read && "bg-rose-50/50")}>
                  <I className={cn("mt-0.5 h-4 w-4 shrink-0", n.kind === "overdue_check" ? "text-amber-600" : n.kind.startsWith("inspector") ? "text-sky-600" : "text-rose-600")} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-900">{n.title}</span>
                    <span className="block truncate text-xs text-slate-500">{n.text}</span>
                    <span className="mt-0.5 block font-mono text-[10px] text-slate-400">{fmtDT(n.created_at)}</span>
                  </span>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-rose-500" />}
                </button>
              </li>
            );
          })}
          {!data.items.length && <li className="px-4 py-8 text-center text-sm text-slate-500">No alerts. All checks are on track.</li>}
        </ul>
        <label className="flex items-center gap-3 border-t border-slate-100 px-4 py-3 text-sm">
          <Mail className="h-4 w-4 shrink-0 text-slate-500" />
          <span className="min-w-0 flex-1"><span className="block font-semibold text-slate-900">Daily email summary</span><span className="block text-xs text-slate-500">{data.has_email ? "Last 24 hours of alerts, sent at 7am" : "Ask your manager to add an email to your account"}</span></span>
          <Switch data-testid="alerts-digest-toggle" checked={!!data.digest} disabled={!data.has_email} onCheckedChange={toggleDigest} />
        </label>
        {user?.tenant_role === "owner" && (
          <label className="flex items-center gap-3 border-t border-slate-100 px-4 py-3 text-sm">
            <CalendarDays className="h-4 w-4 shrink-0 text-slate-500" />
            <span className="min-w-0 flex-1"><span className="block font-semibold text-slate-900">Monday owner report</span><span className="block text-xs text-slate-500">Each venue's score and last week's missed checks, 7am</span></span>
            <Switch data-testid="weekly-report-toggle" checked={!!data.weekly_report} disabled={!data.has_email} onCheckedChange={toggleWeekly} />
          </label>
        )}
      </PopoverContent>
    </Popover>
  );
}
